import { DSSpacer } from '@zoe/ds-web'
import { useEffect, useState } from 'react'
import { Image } from 'react-datocms'
import { styled } from 'styled-components'
import { Autoplay, EffectFade, Pagination, Thumbs } from 'swiper/modules'
import { SwiperSlide } from 'swiper/react'
import { Swiper } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'
import { BentoGalleryGridRecord } from 'types'

import { FragmentComponent } from '@/types/graphql'

import { responsiveImageBlock } from '@/graphql/utils/commonBlocks'

import { BREAKPOINTS, getBreakpointForWidth } from '@/components/ds/breakpoints'

import { useWindowSize } from '@/lib/hooks/useWindowSize'

const fragment = `
  fragment BentoGalleryGridFragment on BentoGalleryGridRecord {
    numberOfColumns
    numberOfRows
    content {
      __typename
      columnSpan
      rowSpan
      image {
        ${responsiveImageBlock()}
      }
    }
  }
`

export const StyledBentoGrid = styled.div<{ $gridCols: number; $gridRows: number; $maxWidth: number }>`
  display: grid;
  gap: var(--grid-16);
  ${({ $gridCols }) => `grid-template-columns: repeat(${$gridCols}, 1fr)`};
  ${({ $gridRows }) => `grid-template-rows: repeat(${$gridRows}, 1fr)`};
  ${({ $maxWidth }) => `max-width: ${$maxWidth}px`};

  margin: 0 auto;

  @media screen and (max-width: ${BREAKPOINTS.s}) {
    gap: var(--grid-8);
    max-width: 100%;
  }
`

const StyledBentoCard = styled.div<{ $col: number; $row: number }>`
  ${({ $col }) => `grid-column: span ${$col}`};
  ${({ $row }) => `grid-row: span ${$row}`};
  border-radius: var(--radius-16);
  overflow: hidden;
  position: relative;
  @media screen and (max-width: ${BREAKPOINTS.s}) {
    border-radius: 0;
  }
`

const StyledImage = styled(Image)`
  object-fit: cover;
  object-position: center;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`

const StyledSwiper = styled(Swiper)<{ $imageCount: number }>`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;

  .swiper-slide {
    border-radius: var(--radius-24);
    overflow: hidden;
  }

  .swiper-pagination {
    display: block;
    bottom: 24px;
    left: calc(50% - ${({ $imageCount }) => (20 + $imageCount * 20) / 2}px);
    width: ${({ $imageCount }) => 20 + $imageCount * 20}px;
    padding: 4px 0;
    border-radius: 20px;
    background-color: var(--color-surface-neutral);
  }

  .swiper-pagination-bullet {
    border-radius: 10px;

    &.swiper-pagination-bullet-active {
      width: 30px;
      background-color: var(--color-surface-navy-bold);
    }
  }
`

const StyledSwiperImage = styled(Image)`
  object-fit: cover;
  object-position: center;
  width: 100%;
  aspect-ratio: 1 / 1;
`

export const BentoGalleryGrid: FragmentComponent<{}, BentoGalleryGridRecord> = ({ record }) => {
  const { width } = useWindowSize()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    console.log('width', getBreakpointForWidth(width ?? 0))
    setIsMobile(getBreakpointForWidth(width ?? 0) === 's')
  }, [width])

  return isMobile ? (
    <>
      <StyledSwiper
        $imageCount={record?.content?.length || 0}
        slidesPerView={1}
        modules={[Thumbs, EffectFade, Pagination, Autoplay]}
        effect="slide"
        autoplay={{ delay: 2500 }}
        pagination={{ clickable: true }}
      >
        {record?.content?.map(({ image }, index) => (
          <SwiperSlide key={index}>
            <StyledSwiperImage data={image?.responsiveImage} />
          </SwiperSlide>
        ))}
      </StyledSwiper>
      <DSSpacer direction="vertical" size={48} />
    </>
  ) : (
    <StyledBentoGrid
      $gridCols={record.numberOfColumns}
      $gridRows={record.numberOfRows}
      $maxWidth={record.maxWidth || 1800}
    >
      {record.content.map(({ image, columnSpan, rowSpan }, index) => (
        <StyledBentoCard key={index} $col={columnSpan} $row={rowSpan}>
          <StyledImage data={image.responsiveImage} />
        </StyledBentoCard>
      ))}
    </StyledBentoGrid>
  )
}

BentoGalleryGrid.fragment = fragment
BentoGalleryGrid.recordName = 'BentoGalleryGridRecord'
